import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, Checkbox, FormControlLabel, FormControl, Grid, MenuItem, Stack, TextField, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions, Box, IconButton, Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";
import CenteredListCard from "../../../components/List/CenteredListCardLayout";
import DefaultActions from "../../../components/Card/DefaultActions";
import { useAuth, useLoading, useNavigation, useSnackbar } from "../../../Context/Contexts";
import { BonusLadderViewModel, BonusRateViewModel, BonusSelectionOption, BonusSystemEditResponse, BonusSystemViewModel } from "../../../Types/ExportTypes";
import { PageComponentProps } from "../../../Types/ExportTypes";
import { AccountAutoComplete, CurrencyAutoComplete } from "../../../components/AutoComplete/AutoCompletes";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import ListCardHeader from "../../../components/List/ListCardHeader";
const defaultBonusSystem: BonusSystemViewModel = {
  id: 0,
  description: "",
  calculationIntervalType: 1,
  bonusValidDays: 0,
  bonusAccountNo: "",
  bonusReservationAccountNo: "",
  reservedVatBonusAccountNo: "",
  bonusDebtAccountNo: "",
  active: true,
};

const BonusSystemEditPage: React.FC<PageComponentProps> = ({ params }) => {
  const { t } = useTranslation();
  const { authService } = useAuth();
  const { showLoading, hideLoading } = useLoading();
  const { showSnackbar } = useSnackbar();
  const { navigate, goBack } = useNavigation();

  const [bonusSystem, setBonusSystem] = useState<BonusSystemViewModel>(defaultBonusSystem);
  const [bonusGroups, setBonusGroups] = useState<BonusSelectionOption[]>([]);
  const [bonusLadder, setBonusLadder] = useState<BonusLadderViewModel[]>([]);
  const [bonusRates, setBonusRates] = useState<BonusRateViewModel[]>([]);
  const [ladderDialogOpen, setLadderDialogOpen] = useState(false);
  const [rateDialogOpen, setRateDialogOpen] = useState(false);
  const [editingLadder, setEditingLadder] = useState<BonusLadderViewModel | null>(null);
  const [editingRate, setEditingRate] = useState<BonusRateViewModel | null>(null);

  const id = params?.id ? Number(params.id) : 0;
  const isCreate = useMemo(() => !id, [id]);

  const loadData = async () => {
    showLoading();
    try {
      const response = await authService.get<BonusSystemEditResponse>(`/bonusApi/BonusSystem?id=${id}`);
      setBonusGroups(response.bonusGroups ?? []);
      if (response.bonusSystem) {
        setBonusSystem(response.bonusSystem);
      } else {
        setBonusSystem(defaultBonusSystem);
      }
    } catch (error: any) {
      showSnackbar(error.response?.data ?? error.message, "error");
    } finally {
      hideLoading();
    }
  };

  const loadBonusLadder = async () => {
    if (isCreate) return;
    showLoading();
    try {
      const response = await authService.get<BonusLadderViewModel[]>(`/bonusApi/BonusLadder?systemId=${id}`);
      setBonusLadder(response);
    } catch (error: any) {
      showSnackbar(error.response?.data ?? error.message, "error");
    } finally {
      hideLoading();
    }
  };

  const loadBonusRates = async () => {
    if (isCreate) return;
    showLoading();
    try {
      const response = await authService.get<BonusRateViewModel[]>(`/bonusApi/BonusRates?systemId=${id}`);
      setBonusRates(response);
    } catch (error: any) {
      showSnackbar(error.response?.data ?? error.message, "error");
    } finally {
      hideLoading();
    }
  };

  useEffect(() => {
    loadData();
  }, [id]);

  const handleSave = async () => {
    showLoading();
    try {
      const response = await authService.post<{ id: number }>("/bonusApi/CreateOrUpdateBonusSystem", bonusSystem);
      showSnackbar(t("dialog.savedSuccessfully"), "success");
      hideLoading();
      navigate({ path: "/sales/bonussystem/edit", params: { id: response.id } });
    } catch (error: any) {
      showSnackbar(error.response?.data ?? error.message, "error");
    } finally {
      hideLoading();
    }
  };

  const handleOpenLadderDialog = async () => {
    if (isCreate) {
      showSnackbar(t("dialog.saveBeforeContinuing"), "warning");
      return;
    }
    await loadBonusLadder();
    setLadderDialogOpen(true);
  };

  const handleOpenRateDialog = async () => {
    if (isCreate) {
      showSnackbar(t("dialog.saveBeforeContinuing"), "warning");
      return;
    }
    await loadBonusRates();
    setRateDialogOpen(true);
  };

  const sortedBonusLadder = useMemo(
    () => [...bonusLadder].sort((a, b) => (a.currencyCode ?? "").localeCompare(b.currencyCode ?? "") || (a.bonusPoints ?? 0) - (b.bonusPoints ?? 0)),
    [bonusLadder]
  );

  const sortedBonusRates = useMemo(
    () => [...bonusRates].sort((a, b) => (a.currencyCode ?? "").localeCompare(b.currencyCode ?? "")),
    [bonusRates]
  );

  const handleSaveLadder = async () => {
    if (!editingLadder) return;
    if (!editingLadder.currencyCode || editingLadder.currencyCode.trim() === "") {
      showSnackbar(t("dialog.currencyCodeRequired"), "warning");
      return;
    }
    if (editingLadder.bonusPoints == null) {
      showSnackbar(t("dialog.valueRequired"), "warning");
      return;
    }
    if (editingLadder.givesBonusAmount == null) {
      showSnackbar(t("dialog.amountRequired"), "warning");
      return;
    }

    showLoading();
    try {
      const payload: BonusLadderViewModel = {
        ...editingLadder,
        bonusSettingsId: id,
        bonusPoints: Number(editingLadder.bonusPoints),
        givesBonusAmount: Number(editingLadder.givesBonusAmount),
      };
      const response = await authService.post<BonusLadderViewModel>("/bonusApi/BonusLadder", payload);
      setBonusLadder((prev) => {
        const existingIndex = prev.findIndex((x) => x.id === response.id);
        if (existingIndex >= 0) {
          const updated = [...prev];
          updated[existingIndex] = response;
          return updated;
        }
        return [...prev, response];
      });
      setEditingLadder(null);
      showSnackbar(t("dialog.savedSuccessfully"), "success");
    } catch (error: any) {
      showSnackbar(error.response?.data ?? error.message, "error");
    } finally {
      hideLoading();
    }
  };

  const handleDeleteLadder = async (ladder: BonusLadderViewModel) => {
    if (!ladder.id) return;
    showLoading();
    try {
      await authService.post(`/bonusApi/DeleteBonusLadder/?id=${ladder.id}`, {});
      setBonusLadder((prev) => prev.filter((x) => x.id !== ladder.id));
      showSnackbar(t("dialog.deletedSuccessfully"), "success");
    } catch (error: any) {
      showSnackbar(error.response?.data ?? error.message, "error");
    } finally {
      hideLoading();
    }
  };

  const handleSaveRate = async () => {
    if (!editingRate) return;
    if (!editingRate.currencyCode || editingRate.currencyCode.trim() === "") {
      showSnackbar(t("dialog.currencyCodeRequired"), "warning");
      return;
    }

    showLoading();
    try {
      const payload: BonusRateViewModel = {
        ...editingRate,
        bonusSettingsId: id,
        bonusPointExchangeRate:
          editingRate.bonusPointExchangeRate === null || editingRate.bonusPointExchangeRate === undefined
            ? null
            : Number(editingRate.bonusPointExchangeRate),
      };
      const response = await authService.post<BonusRateViewModel>("/bonusApi/BonusRate", payload);
      setBonusRates((prev) => {
        const existingIndex = prev.findIndex((x) => x.currencyCode === response.currencyCode);
        if (existingIndex >= 0) {
          const updated = [...prev];
          updated[existingIndex] = response;
          return updated;
        }
        return [...prev, response];
      });
      setEditingRate(null);
      showSnackbar(t("dialog.savedSuccessfully"), "success");
    } catch (error: any) {
      showSnackbar(error.response?.data ?? error.message, "error");
    } finally {
      hideLoading();
    }
  };

  return (
    <CenteredListCard title={t("common.bonusSystem")}>
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
        <DefaultActions onSave={handleSave} onCancel={goBack} />
      </Stack>
      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <Button variant="contained" onClick={handleOpenLadderDialog} startIcon={<EditIcon />} disabled={isCreate}>
          {t("common.bonusLadder")}
        </Button>
        <Button variant="contained" onClick={handleOpenRateDialog} startIcon={<EditIcon />} disabled={isCreate}>
          {t("common.bonusRate")}
        </Button>
      </Stack>
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {isCreate ? t("common.create") : t("common.edit")}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                required
                fullWidth
                label={t("common.description")}
                value={bonusSystem.description}
                onChange={(e) => setBonusSystem({ ...bonusSystem, description: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label={t("common.bonusGroup")}
                value={bonusSystem.bonusGroupId ?? ""}
                onChange={(e) => setBonusSystem({ ...bonusSystem, bonusGroupId: e.target.value ? Number(e.target.value) : null })}
                InputLabelProps={{ shrink: true }}
              >
                <MenuItem value="">{t("common.select")}</MenuItem>
                {bonusGroups.map((group) => (
                  <MenuItem key={group.id} value={group.id}>
                    {group.description}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label={t("common.calculationInterval")}
                value={bonusSystem.calculationIntervalType}
                onChange={(e) => setBonusSystem({ ...bonusSystem, calculationIntervalType: Number(e.target.value) })}
                InputLabelProps={{ shrink: true }}
              >
                <MenuItem value={0}>{t("common.continuous")}</MenuItem>
                <MenuItem value={1}>{t("common.period")}</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                type="date"
                fullWidth
                label={t("common.startDate")}
                value={bonusSystem.startDate ? new Date(bonusSystem.startDate).toISOString().slice(0, 10) : ""}
                onChange={(e) => setBonusSystem({ ...bonusSystem, startDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label={t("common.validDays")}
                value={bonusSystem.bonusValidDays}
                onChange={(e) => setBonusSystem({ ...bonusSystem, bonusValidDays: Number(e.target.value) })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <AccountAutoComplete
                  key={bonusSystem.bonusAccountNo ?? ""}
                  label={t("common.bonusAccount")}
                  valueId={bonusSystem.bonusAccountNo ?? ""}
                  onChange={(selected) => {
                    setBonusSystem({
                      ...bonusSystem, bonusAccountNo: selected?.value ?? null
                    });
                  }}
                  load
                />
              </FormControl>

            </Grid>
            <Grid item xs={12} md={6}>

              <FormControl fullWidth>
                <AccountAutoComplete
                  key={bonusSystem.bonusReservationAccountNo ?? ""}
                  label={t("common.reservationAccount")}
                  valueId={bonusSystem.bonusReservationAccountNo ?? ""}
                  onChange={(selected) => {
                    setBonusSystem({
                      ...bonusSystem, bonusReservationAccountNo: selected?.value ?? null
                    });
                  }}
                  load
                />
              </FormControl>

            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <AccountAutoComplete
                  key={bonusSystem.reservedVatBonusAccountNo ?? ""}
                  label={t("common.reservedVatAccount")}
                  valueId={bonusSystem.reservedVatBonusAccountNo ?? ""}
                  onChange={(selected) => {
                    setBonusSystem({
                      ...bonusSystem, reservedVatBonusAccountNo: selected?.value ?? null
                    });
                  }}
                  load
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>

              <FormControl fullWidth>
                <AccountAutoComplete
                  key={bonusSystem.bonusDebtAccountNo ?? ""}
                  label={t("common.debtAccount")}
                  valueId={bonusSystem.bonusDebtAccountNo ?? ""}
                  onChange={(selected) => {
                    setBonusSystem({
                      ...bonusSystem, bonusDebtAccountNo: selected?.value ?? null
                    });
                  }}
                  load
                />
              </FormControl>

            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!bonusSystem.active}
                    onChange={(e) => setBonusSystem({ ...bonusSystem, active: e.target.checked })}
                  />
                }
                label={t("common.active")}
              />
            </Grid>
            {/*<Grid item xs={12}>*/}
            {/*  <FormControlLabel*/}
            {/*    control={*/}
            {/*      <Checkbox*/}
            {/*        checked={!!bonusSystem.calculateNonContinuousBonusFromPrelim}*/}
            {/*        onChange={(e) =>*/}
            {/*          setBonusSystem({ ...bonusSystem, calculateNonContinuousBonusFromPrelim: e.target.checked })*/}
            {/*        }*/}
            {/*      />*/}
            {/*    }*/}
            {/*    label={t("common.calculateFromPreliminary")}*/}
            {/*  />*/}
            {/*</Grid>*/}
          </Grid>
        </CardContent>
      </Card>
      <Dialog
        open={ladderDialogOpen}
        onClose={() => setLadderDialogOpen(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: "8px" } }}
      >
        <DialogTitle>{t("common.bonusLadder")}</DialogTitle>
        <DialogContent dividers>
          <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() =>
                setEditingLadder({
                  bonusSettingsId: id,
                  currencyCode: bonusLadder[0]?.currencyCode ?? "",
                  bonusPoints: 0,
                  givesBonusAmount: 0,
                })
              }
            >
              {t("common.add")}
            </Button>
          </Stack>
          {sortedBonusLadder.length === 0 ? (
            <Typography color="text.secondary">{t("common.noData")}</Typography>
          ) : (
            <Grid container spacing={2}>
              {sortedBonusLadder.map((ladder) => (
                <Grid item xs={12} md={6} key={ladder.id ?? `${ladder.currencyCode}-${ladder.bonusPoints}`}>
                  <Grid
                    container
                    spacing={1}
                    sx={{
                      p: 1,
                      border: "1px solid #ccc",
                      borderRadius: 1,
                      cursor: "pointer",
                      ":hover": { backgroundColor: "#f0f0f0" },
                    }}
                  >
                    <Grid item xs={10}>
                      <ListCardHeader
                        icon={<AddIcon fontSize="small" color="action" />}
                        title={`${ladder.currencyCode ?? ""} - ${ladder.bonusPoints ?? 0}`}
                        status={{ label: t("common.bonusPoints"), color: "info" }}
                        hasEdit
                      />
                      <Typography variant="body2">{`${t("common.currency")}: ${ladder.currencyCode ?? t("common.notSet")}`}</Typography>
                      <Typography variant="body2">{`${t("common.bonusPoints")}: ${ladder.bonusPoints ?? 0}`}</Typography>
                      <Typography variant="body2">{`${t("common.givesBonusAmount")}: ${ladder.givesBonusAmount ?? 0}`}</Typography>
                    </Grid>
                    <Grid item xs={2}>
                      <Stack spacing={1} direction="row" justifyContent="flex-end">
                        <Tooltip title={t("common.edit")}>
                          <IconButton size="small" color="primary" onClick={() => setEditingLadder(ladder)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t("common.delete")}>
                          <IconButton size="small" color="error" onClick={() => handleDeleteLadder(ladder)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Grid>
                  </Grid>
                </Grid>
              ))}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLadderDialogOpen(false)} startIcon={<CloseIcon />}>{t("common.cancel")}</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!editingLadder}
        onClose={() => setEditingLadder(null)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: "8px" } }}
      >
        <DialogTitle>
          {editingLadder?.id ? `${t("common.edit")} ${t("common.bonusLadder")}` : `${t("common.add")} ${t("common.bonusLadder")}`}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth>
              <CurrencyAutoComplete
                key={editingLadder?.currencyCode ?? ""}
                label={t("common.currencyCode")}
                valueId={editingLadder?.currencyCode ?? ""}
                onChange={(selected) => setEditingLadder(editingLadder ? { ...editingLadder, currencyCode: selected?.value ?? null } : null)}
                load
                selectFirst
              />
            </FormControl>
            <TextField
              type="number"
              label={t("common.bonusPoints")}
              value={editingLadder?.bonusPoints ?? ""}
              onChange={(e) =>
                setEditingLadder(
                  editingLadder
                    ? { ...editingLadder, bonusPoints: e.target.value === "" ? null : Number(e.target.value) }
                    : null
                )
              }
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              type="number"
              label={t("common.givesBonusAmount")}
              value={editingLadder?.givesBonusAmount ?? ""}
              onChange={(e) =>
                setEditingLadder(
                  editingLadder
                    ? { ...editingLadder, givesBonusAmount: e.target.value === "" ? null : Number(e.target.value) }
                    : null
                )
              }
              InputLabelProps={{ shrink: true }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingLadder(null)} startIcon={<CloseIcon />}>{t("common.cancel")}</Button>
          <Button onClick={handleSaveLadder} startIcon={<SaveIcon />} variant="contained">
            {t("common.save")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={rateDialogOpen}
        onClose={() => setRateDialogOpen(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: "8px" } }}
      >
        <DialogTitle>{t("common.bonusRate")}</DialogTitle>
        <DialogContent dividers>
          {sortedBonusRates.length === 0 ? (
            <Typography color="text.secondary">{t("common.noData")}</Typography>
          ) : (
            <Stack spacing={2}>
              {sortedBonusRates.map((rate) => (
                <Box key={rate.id ?? rate.currencyCode} sx={{ border: "1px solid #ccc", borderRadius: 1, p: 2 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="subtitle1">{rate.currencyCode}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t("common.bonusPointExchangeRate")}: {rate.bonusPointExchangeRate ?? t("common.notSet")}
                      </Typography>
                      {rate.comment && (
                        <Typography variant="caption" color="text.secondary">
                          {rate.comment}
                        </Typography>
                      )}
                    </Box>
                    <Button variant="outlined" startIcon={<EditIcon />} onClick={() => setEditingRate(rate)}>
                      {t("common.edit")}
                    </Button>
                  </Stack>
                </Box>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRateDialogOpen(false)} startIcon={<CloseIcon />}>{t("common.cancel")}</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!editingRate}
        onClose={() => setEditingRate(null)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: "8px" } }}
      >
        <DialogTitle>
          {editingRate?.id ? `${t("common.edit")} ${t("common.bonusRate")}` : `${t("common.add")} ${t("common.bonusRate")}`}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label={t("common.currencyCode")}
              value={editingRate?.currencyCode ?? ""}
              onChange={(e) => setEditingRate(editingRate ? { ...editingRate, currencyCode: e.target.value } : null)}
              InputLabelProps={{ shrink: true }}
              disabled
            />
            <TextField
              type="number"
              label={t("common.bonusPointExchangeRate")}
              value={editingRate?.bonusPointExchangeRate ?? ""}
              onChange={(e) =>
                setEditingRate(
                  editingRate
                    ? {
                      ...editingRate,
                      bonusPointExchangeRate: e.target.value === "" ? null : Number(e.target.value),
                    }
                    : null
                )
              }
              InputLabelProps={{ shrink: true }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingRate(null)} startIcon={<CloseIcon />}>{t("common.cancel")}</Button>
          <Button onClick={handleSaveRate} startIcon={<SaveIcon />} variant="contained">
            {t("common.save")}
          </Button>
        </DialogActions>
      </Dialog>
    </CenteredListCard>
  );
};

export default BonusSystemEditPage;
